import * as React from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { Fabric, PrimaryButton } from '@fluentui/react';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { useBetreuer } from 'api/betreuer';
import { ValueType } from 'react-select';
import { SelectOption } from 'formComponents/Select/types';
import { Select } from 'formComponents/Select/Select';
import { authSelector } from 'store/auth/selector';
import { TerminFilter, useTerminList } from 'api/termin';
import { TerminEdit, TerminEditData } from 'components/Termin/TerminEdit';
import { useKundeList } from 'api/kunde';
import { TerminItem } from 'components/Termin/TerminItem';
import { viewStyles } from 'styles/viewStyles';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import { formStyles } from 'styles/formStyles';
import usePersistentState from 'hooks/usePersistentState';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import moment from 'moment-timezone';
import useDebouncedState from 'hooks/useDebouncedState';
import { DebouncedSearchBox } from 'formComponents/DebouncedSearchBox';
import { useHistory, useParams } from 'react-router-dom';
import { RecordViewUrlParams } from 'layout/App/types';
import { createRecordUrl } from 'utils/formatter';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';
import { TerminArt } from 'formComponents/lookups/TerminArt';

const defaultMaxRows = 250;

const styles = mergeStyleSets({
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '250px 150px 150px 200px 150px 150px 1fr max-content max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall1: {
        display: 'grid',
        gridTemplateColumns: '250px 150px 200px 200px 1fr max-content max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBarSmall2: {
        display: 'grid',
        gridTemplateColumns: '150px 150px 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    listContainer: {
        overflow: 'scroll',
    },
});

const terminStatusFilterItems: SelectOption[] = [
    { value: 'onlyOpen', label: 'Nur unerledigte' },
    { value: 'onlyDue', label: 'Nur fällige' },
    { value: 'all', label: 'Alle' },
];

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

interface FilterValues {
    maxRows: number;
    kNr?: number;
    nr?: string;
    status: SelectOption;
    art: string;
    betreuer: SelectOption;
    datumVon?: string;
    datumBis?: string;
    searchText: string;
    latestFirst: boolean;
}

const defaultFilterValues: FilterValues = {
    maxRows: defaultMaxRows,
    kNr: undefined,
    nr: undefined,
    status: terminStatusFilterItems[2],
    art: '__all__',
    betreuer: alleBetreuerOption,
    datumVon: undefined,
    datumBis: undefined,
    searchText: '',
    latestFirst: true,
};

export const TerminView = (): JSX.Element => {
    const auth = useSelector(authSelector);
    const history = useHistory();
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const [viewMode, setViewMode] = usePersistentState<'short' | 'long'>('long', 'TerminView.viewMode');

    const { urlKundeNr, urlRecordNr } = useParams<RecordViewUrlParams>();
    const kNr = urlKundeNr ? parseInt(urlKundeNr, 10) : undefined;

    const isSmallScreenEmbedded = useMediaQuery({ query: `(max-width: 1800px)` });
    const isSmallScreenNotEmbedded = useMediaQuery({ query: `(max-width: 1450px)` });
    const isSmallScreen = urlKundeNr ? isSmallScreenEmbedded : isSmallScreenNotEmbedded;

    const terminEditData = React.useMemo<TerminEditData | null>(() => {
        if (urlRecordNr) {
            return {
                zfKaNr: parseInt(urlRecordNr, 10),
                kNr,
            };
        }
        return null;
    }, [kNr, urlRecordNr]);

    const kundeList = useKundeList({ nr: kNr || -1 });

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [alleBetreuerOption];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.kz || '',
                label: auth.name || '?',
            });
        } else {
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.kz, label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(() => {
        if (auth.rolle === 'AD' || !urlKundeNr) {
            return {
                ...defaultFilterValues,
                status: terminStatusFilterItems[1],
                betreuer: {
                    value: auth.kz || '',
                    label: auth.name || '?',
                },
            };
        }
        return {
            ...defaultFilterValues,
            betreuer: alleBetreuerOption,
        };
    }, 1000);

    const filter = React.useMemo<TerminFilter>(() => {
        return {
            kNr,
            userKz:
                debouncedFilterValues.betreuer.value === '__all__' ? undefined : debouncedFilterValues.betreuer.value,
            status: -1,
            terminArt: debouncedFilterValues.art === '__all__' ? undefined : debouncedFilterValues.art,
            datumVon: debouncedFilterValues.datumVon,
            datumBis: debouncedFilterValues.datumBis,
            searchText: debouncedFilterValues.searchText,
            sortAsc: !debouncedFilterValues.latestFirst,
            withKunde: true,
            maxRows: defaultMaxRows,
        };
    }, [kNr, debouncedFilterValues]);

    const offeneTermineFilter = React.useMemo<TerminFilter>(
        () => ({
            ...filter,
            terminStatus: 0,
            onlyDue: debouncedFilterValues.status.value === 'onlyDue',
        }),
        [filter, debouncedFilterValues.status.value],
    );

    const offeneTermine = useTerminList(offeneTermineFilter, { enabled: false });

    const erledigteTermineFilter = React.useMemo<TerminFilter>(
        () => ({
            ...filter,
            terminStatus: 1,
        }),
        [filter],
    );

    const erledigteTermine = useTerminList(erledigteTermineFilter, { enabled: false });

    React.useEffect(() => {
        if (
            debouncedFilterValues.status.value === 'all' &&
            !erledigteTermine.isFetched &&
            !erledigteTermine.isFetching &&
            !erledigteTermine.isLoading
        ) {
            erledigteTermine.refetch();
        }

        if (!offeneTermine.isFetched && !offeneTermine.isFetching && !offeneTermine.isLoading) {
            offeneTermine.refetch();
        }
    }, [debouncedFilterValues.status, offeneTermine, erledigteTermine]);

    const renderDatumVon = () => (
        <FormDatePicker
            value={filterValues.datumVon}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    const renderDatumBis = () => (
        <FormDatePicker
            value={filterValues.datumBis}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    if (auth.rolle === 'AD' && kundeList.data?.length === 1) {
        if (kundeList.data[0].betreuer && kundeList.data[0].betreuer !== auth.nr) {
            return (
                <Fabric className={viewStyles.container}>
                    <div className={styles.listContainer}>
                        <div className={viewStyles.noData}>Keine Daten.</div>
                    </div>
                </Fabric>
            );
        }
    }

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric
            className={classnames(viewStyles.container, {
                [viewStyles.twoSearchBarLines]: isSmallScreen,
                [viewStyles.embedded]: !!urlKundeNr,
            })}
        >
            <Helmet>
                <title>
                    {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                    Tätigkeiten
                </title>
            </Helmet>
            <div className={isSmallScreen ? styles.searchBarSmall1 : styles.searchBar}>
                <DebouncedSearchBox
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            searchText: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <Select
                    name="terminStatusFilter"
                    value={filterValues.status}
                    options={terminStatusFilterItems}
                    onChange={(newValue: ValueType<SelectOption, false>): void => {
                        setFilterValues({
                            ...filterValues,
                            status: newValue || defaultFilterValues.status,
                        });
                    }}
                />
                <TerminArt
                    renderLabel="placeholder"
                    value={filterValues.art}
                    addAll
                    onChange={(newValue): void => {
                        setFilterValues({
                            ...filterValues,
                            art: newValue || defaultFilterValues.art,
                        });
                    }}
                />
                <Select
                    name="betreuer"
                    value={filterValues.betreuer}
                    options={betreuerFilterOptions}
                    onChange={(newValue: ValueType<SelectOption, false>): void => {
                        setFilterValues({
                            ...filterValues,
                            betreuer: newValue || defaultFilterValues.betreuer,
                        });
                    }}
                />

                {isSmallScreen || renderDatumVon()}
                {isSmallScreen || renderDatumBis()}

                <div />
                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Neueste zuerst"
                        checked={filterValues.latestFirst}
                        onChange={(event, checked) => {
                            setFilterValues({
                                ...filterValues,
                                latestFirst: !!checked,
                            });
                        }}
                    />
                </div>
                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Kurzansicht"
                        checked={viewMode === 'short'}
                        onChange={(event, checked) => {
                            setViewMode(checked ? 'short' : 'long');
                        }}
                    />
                </div>
                {kNr && (
                    <PrimaryButton
                        onClick={() => history.push(createRecordUrl('taetigkeiten', urlKundeNr, '-1'))}
                        text="Neue Tätigkeit"
                    />
                )}
            </div>

            {isSmallScreen && (
                <div className={styles.searchBarSmall2}>
                    {renderDatumVon()}
                    {renderDatumBis()}
                </div>
            )}

            {offeneTermine.isLoading ||
            offeneTermine.isFetching ||
            erledigteTermine.isLoading ||
            erledigteTermine.isFetching ? (
                <LoadingIndicator />
            ) : (
                <>
                    <div className={styles.listContainer}>
                        {offeneTermine?.data && offeneTermine?.data.length === 0 ? (
                            <div className={viewStyles.noData}>Keine offenen Tätigkeiten.</div>
                        ) : (
                            offeneTermine?.data?.map((termin) => (
                                <TerminItem
                                    key={termin.zfKaNr}
                                    withKunde={!urlKundeNr}
                                    termin={termin}
                                    viewMode={viewMode}
                                    editTermin={() =>
                                        history.push(
                                            createRecordUrl(
                                                'taetigkeiten',
                                                urlKundeNr,
                                                (termin.zfKaNr || 0).toString(),
                                            ),
                                        )
                                    }
                                />
                            ))
                        )}
                        {debouncedFilterValues.status.value === 'all' &&
                            (erledigteTermine?.data && erledigteTermine?.data.length === 0 ? (
                                <div className={viewStyles.noData}>Keine abgeschlossenen Tätigkeiten.</div>
                            ) : (
                                erledigteTermine?.data?.map((termin) => (
                                    <TerminItem
                                        key={termin.zfKaNr}
                                        withKunde={!urlKundeNr}
                                        termin={termin}
                                        viewMode={viewMode}
                                        editTermin={() =>
                                            history.push(
                                                createRecordUrl(
                                                    'taetigkeiten',
                                                    urlKundeNr,
                                                    (termin.zfKaNr || 0).toString(),
                                                ),
                                            )
                                        }
                                    />
                                ))
                            ))}
                    </div>
                </>
            )}
            {terminEditData !== null && (
                <TerminEdit
                    defaultKNr={terminEditData.kNr}
                    zfKaNr={terminEditData.zfKaNr}
                    onClose={(refetch) => {
                        history.push(createRecordUrl('taetigkeiten', urlKundeNr));
                        if (refetch) {
                            if (debouncedFilterValues.status.value === 'all') {
                                erledigteTermine.refetch().then();
                            }
                            offeneTermine.refetch().then();
                        }
                    }}
                />
            )}
        </Fabric>
    );
};
